import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Form } from '@/_shared/components/Form';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { useNextRechargeInDays } from '@/_shared/hooks/useNextRechargeInDays';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentsCreditsCall, getDepartmentsCreditsQuarterCall } from '../../state';
import {
  ClusterDataDepartment,
  DivisionByTeam,
  DivisionByTrendDepartment,
  RecapDataDepartment,
} from './Department';
import { EmptyState } from './EmptyState';

type Props = {
  departmentId?: string;
  departmentName?: string;
};

export const TabCreditsDepartment: FC<Props> = ({ departmentName, departmentId }) => {
  const t = useTranslations('departments.tabs.credits.recap');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    execute: executeGetDepartmentsCredits,
    loading: loadingGetDepartmentsCredits,
    success: successGetDepartmentsCredits,
    error: errorGetDepartmentsCredits,
    hasNoData,
  } = getDepartmentsCreditsCall.hook();

  const {
    execute: executeGetDepartmentsCreditsQuarter,
    loading: loadingGetDepartmentsCreditsQuarter,
    error: errorGetDepartmentsCreditsQuarter,
    defaultQuarter,
    response: responseGetDepartmentsCreditsQuarter,
  } = getDepartmentsCreditsQuarterCall.hook();

  const { nextMonth, days } = useNextRechargeInDays();

  const { control, watch } = useForm({
    defaultValues: {
      filterQuarter: {
        value: defaultQuarter || '',
      },
    },
  });

  const fieldValues = watch();

  useEffect(() => {
    executeGetDepartmentsCreditsQuarter({
      departmentId: departmentId || '',
      successSaga: ({ response }) => {
        if (response.length === 0) {
          return;
        }
        if (response[0].year && response[0].quarter) {
          executeGetDepartmentsCredits({
            departmentId: departmentId || '',
            year: response[0].year,
            quarter: response[0].quarter,
          });
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  useEffect(() => {
    if (fieldValues.filterQuarter) {
      const [quarter, year] = fieldValues.filterQuarter.value.split('-');
      if (quarter !== 'undefined' && quarter !== '' && year !== 'undefined' && year !== '') {
        executeGetDepartmentsCredits({
          departmentId: departmentId || '',
          year: Number(year),
          quarter: Number(quarter),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, fieldValues.filterQuarter]);

  if (loadingGetDepartmentsCredits || loadingGetDepartmentsCreditsQuarter) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader color='primary' size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentsCreditsQuarter || responseGetDepartmentsCreditsQuarter?.length === 0) {
    return <EmptyState />;
  }

  if (errorGetDepartmentsCredits || errorGetDepartmentsCreditsQuarter) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    successGetDepartmentsCredits &&
    !loadingGetDepartmentsCredits &&
    !errorGetDepartmentsCredits
  ) {
    return (
      <Box>
        {hasNoData ? (
          <EmptyState />
        ) : (
          <Box>
            <Box
              mt='m'
              mb='s'
              display='flex'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
              justifyContent='space-between'
            >
              <Typography
                variant='h1'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                {departmentName}
              </Typography>
              <Typography variant='p2/light'>
                <RenderLocalesWithComponents
                  text={t('nextRecharge', {
                    date: '!{DATE}!',
                    days: days.toString(),
                  })}
                  placeholders={{
                    '!{DATE}!': (
                      <Typography as='span' variant='p2/light' fontWeight='500'>
                        {nextMonth}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant='p3/light'>{t('description')}</Typography>
            </Box>
            <Box width='300px' mt='s' mb='m'>
              {responseGetDepartmentsCreditsQuarter && (
                <Form.FormDropdown
                  // @ts-ignore
                  control={control}
                  name='filterQuarter'
                  items={
                    responseGetDepartmentsCreditsQuarter
                      ? responseGetDepartmentsCreditsQuarter.map(item => ({
                          label: `Q${item.quarter} - ${item.year}`,
                          value: `${item.quarter}-${item.year}`,
                        }))
                      : []
                  }
                  labelStyle={{ color: 'white', mb: 'xxs' }}
                  data-testid='dropdown-select-campaign'
                  optionTestId='dropdown-select-campaign-option'
                  placeholder={
                    `Q${responseGetDepartmentsCreditsQuarter?.[0].quarter} - ${responseGetDepartmentsCreditsQuarter?.[0].year}` ||
                    ''
                  }
                />
              )}
            </Box>
            <RecapDataDepartment />
            <ClusterDataDepartment />
            <DivisionByTeam />
            <DivisionByTrendDepartment departmentName={departmentName || ''} />
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
